import type { AccountSource } from "owa-account-source-list-store";
import { accountSourceDataTypeChecker, isFirstPartyAccountSourceType, } from "owa-account-source-list-store";
import { getAuthConfig } from "owa-auth-callbacks";
import { getMsalInstance } from "./initializeMsalLibrary";
import { getAccountScopeUserSettings } from "owa-session-store";
import type { MailboxInfo } from "owa-client-types";
import { getAccountFromMsal } from "./utils/MsalAccounts";
import { logStartGreyError } from "owa-analytics-start";
import { type TraceErrorObject } from "owa-analytics-types";

/**
 * Registration for login_hint caching for OWA
 * This gets called automatically when any account reaches StartupComplete state.
 *
 * Behavior:
 * - Gets login_hint from MSAL account.idTokenClaims
 * - Falls back to SessionSettings.LoginHint
 * - Skips cloud cache accounts (Google Calendar) which don't have
 *   their own first-party identity and would incorrectly pick up the primary
 *   account's login_hint claim.
 *
 * Works for all apps: OWA, Places, Newsletters, etc.
 */
function onAccountAdded(accountSource: AccountSource): void {
    if (!isFirstPartyAccountSourceType(accountSource.sourceType)) {
        return;
    }
    // Skip caching the login_hint claim for connected accounts in OWA. Their
    // tokens are brokered differently and they would otherwise incorrectly pick
    // up the primary account's login_hint claim.
    if (accountSourceDataTypeChecker.isM365ConnectedMailbox(accountSource)) {
        return;
    }
    setLoginHintFromMsalForAccount(accountSource.mailboxInfo);
}

function onAccountRemoved(accountSource: AccountSource): void {
    if (!isFirstPartyAccountSourceType(accountSource.sourceType)) {
        return;
    }
    // Delete login_hint for removed accounts
    getAuthConfig().deleteLoginHintClaim?.(accountSource.mailboxInfo);
}

async function setLoginHintFromMsalForAccount(mailboxInfo: MailboxInfo): Promise<void> {
    let loginHint: string | undefined;
    let sessionLoginHint: string | undefined;
    
    // Get MSAL account
    const msalInstance = await getMsalInstance();
    const account = getAccountFromMsal(msalInstance, mailboxInfo);
    
    if (account) {
        loginHint = account.idTokenClaims?.login_hint as string | undefined;
    }
    
    // Fallback to SessionSettings.LoginHint if MSAL didn't provide it or account didn't match
    if (!loginHint) {
        const userSettings = getAccountScopeUserSettings(mailboxInfo);
        sessionLoginHint = userSettings.SessionSettings?.LoginHint;
        loginHint = sessionLoginHint;
    }
    
    if (loginHint) {
        // Populate the in-memory cache with proper mailboxInfo indexer.
        getAuthConfig().setLoginHintClaim?.(mailboxInfo, loginHint);
    }
    else {
        const error: TraceErrorObject = new Error("login_hint not available from MSAL account or SessionSettings");
        error.additionalInfo = {
            hasMsalAccount: !!account,
            hasMsalIdTokenClaims: !!account?.idTokenClaims,
            hasMsalLoginHint: !!account?.idTokenClaims?.login_hint,
            hasSessionLoginHint: !!sessionLoginHint,
        };
        logStartGreyError("SetLoginHintClaimSkipped", mailboxInfo, error);
    }
}

export function registerLoginHintCacheForAccountChanges(registerFn: (accountStartupComplete: (accountSource: AccountSource) => void, preAccountRemoved: (accountSource: AccountSource) => void) => void): void {
    registerFn(onAccountAdded, onAccountRemoved);
}
